import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { track } from "../../lib/analytics";

export const CANCELLATION_REASONS = [
  "unclear_usage", "quote_process_complex", "quote_too_slow", "machine_setup_complex",
  "quote_not_accurate", "quote_not_useful", "ai_trust", "expected_different",
  "missing_features", "navigation_hard", "quotes_hard_to_find", "technical_issues",
  "no_advantage", "just_testing", "no_need_now", "other",
];

export const EXPECTED_IMPROVEMENTS = [
  "more_accurate_quotes", "more_detailed_ai", "more_speed", "simpler_machines",
  "better_quote_management", "better_pdf_export", "more_customization",
  "more_workshop_features", "integrations", "better_price", "dont_know", "other",
];

function CheckItem({ checked, onToggle, label, testid }) {
  return (
    <label
      className={`flex items-start gap-2.5 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors ${
        checked
          ? "border-amber-500/60 bg-amber-500/10 text-slate-100"
          : "border-slate-700 bg-slate-900/40 text-slate-300 hover:border-slate-500"
      }`}
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 shrink-0 accent-amber-500 cursor-pointer"
      />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

export default function DeleteAccountSection() {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const [stage, setStage] = useState("idle"); // idle | feedback | confirm
  const [reasons, setReasons] = useState([]);
  const [improvements, setImprovements] = useState([]);
  const [comment, setComment] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const toggle = (list, setList, code) =>
    setList(list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);

  const reset = () => {
    setStage("idle"); setReasons([]); setImprovements([]); setComment(""); setConfirmText(""); setError("");
  };

  const start = () => {
    track("account_delete_started");
    setStage("feedback");
  };

  const cancel = () => {
    track("account_delete_cancelled");
    reset();
  };

  const continueToConfirm = () => {
    if (reasons.length || improvements.length || comment.trim()) {
      track("account_delete_feedback_submitted", {
        cancellation_reasons: reasons,
        expected_improvements: improvements,
        has_comment: !!comment.trim(),
        language: (i18n.language || "it").split("-")[0],
      });
    }
    setStage("confirm");
  };

  const deleteAccount = async () => {
    if (confirmText !== t("settings.confirmWord", "ELIMINA")) return;
    setDeleting(true); setError("");
    try {
      await api.delete("/me/account", {
        data: {
          confirm: "DELETE_MY_ACCOUNT",
          feedback: {
            cancellation_reasons: reasons,
            expected_improvements: improvements,
            additional_comment: comment.trim(),
          },
        },
      });
      track("account_deleted");
      await logout();
      window.location.replace("/");
    } catch (e) {
      setError(e.response?.data?.detail || t("common.loadingError"));
      setDeleting(false);
    }
  };

  if (stage === "idle") {
    return (
      <button
        onClick={start}
        className="px-4 py-2 rounded-md bg-red-600/10 hover:bg-red-600/20 text-red-300 border border-red-500/40 text-sm flex items-center gap-2"
        data-testid="delete-account-btn"
      >
        <Trash2 size={14}/> {t("settings.deleteAccountBtn")}
      </button>
    );
  }

  if (stage === "feedback") {
    return (
      <div className="space-y-5" data-testid="delete-feedback-step">
        <div>
          <h3 className="font-display font-semibold text-slate-100 text-base">{t("deleteFeedback.title")}</h3>
          <p className="text-xs text-slate-500 mt-1">{t("deleteFeedback.text")}</p>
        </div>

        <div>
          <div className="label-tech mb-2">{t("deleteFeedback.question")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CANCELLATION_REASONS.map((code) => (
              <CheckItem
                key={code}
                checked={reasons.includes(code)}
                onToggle={() => toggle(reasons, setReasons, code)}
                label={t(`deleteFeedback.reasons.${code}`)}
                testid={`reason-${code}`}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="label-tech mb-2">{t("deleteFeedback.improvementsQuestion")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {EXPECTED_IMPROVEMENTS.map((code) => (
              <CheckItem
                key={code}
                checked={improvements.includes(code)}
                onToggle={() => toggle(improvements, setImprovements, code)}
                label={t(`deleteFeedback.improvements.${code}`)}
                testid={`improvement-${code}`}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="label-tech block mb-2">{t("deleteFeedback.commentLabel")}</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={t("deleteFeedback.commentPlaceholder")}
            className="w-full px-4 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-slate-500 resize-y"
            data-testid="delete-feedback-comment"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={cancel}
            className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm border border-slate-700"
            data-testid="delete-feedback-cancel-btn"
          >
            {t("settings.deleteCancel")}
          </button>
          <button
            onClick={continueToConfirm}
            className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm flex items-center gap-2"
            data-testid="delete-feedback-continue-btn"
          >
            <Trash2 size={14}/> {t("deleteFeedback.continueBtn")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="delete-account-confirm">
      <div className="p-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-start gap-2">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
        <div>
          <strong>{t("settings.deleteWarning")}</strong><br/>
          {t("settings.deleteBody")}
        </div>
      </div>
      <div>
        <label className="label-tech block mb-2">{t("settings.deleteConfirm")}</label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={t("settings.confirmWord", "ELIMINA")}
          className="w-full px-4 py-2 rounded-md bg-slate-900 border border-red-500/40 text-slate-100 text-sm focus:outline-none focus:border-red-500"
          data-testid="delete-confirm-input"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={cancel}
          className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm border border-slate-700"
          data-testid="delete-cancel-btn"
        >
          {t("settings.deleteCancel")}
        </button>
        <button
          onClick={deleteAccount}
          disabled={confirmText !== t("settings.confirmWord", "ELIMINA") || deleting}
          className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="delete-confirm-btn"
        >
          <Trash2 size={14}/> {deleting ? t("common.loading") : t("settings.deleteFinal")}
        </button>
      </div>
      {error && <div className="p-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="delete-error">{error}</div>}
    </div>
  );
}
